import React, { createContext, useState, useEffect, useContext } from 'react';
import { getApiUrl } from '../config';
import config from '../config';
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSession = async () => {
      try {
        const savedToken = localStorage.getItem('authToken');
        const savedUser = localStorage.getItem('authUser');
        const rememberMe = localStorage.getItem('rememberMe') === 'true';

        const sessionToken = sessionStorage.getItem('authToken');
        const sessionUser = sessionStorage.getItem('authUser');

        const currentToken = savedToken || sessionToken;
        const currentUser = savedUser ? JSON.parse(savedUser) : (sessionUser ? JSON.parse(sessionUser) : null);

        if (currentToken && currentUser) {
          const response = await fetch(getApiUrl(config.api.endpoints.auth.validateToken), {
            headers: {
              'Authorization': `Bearer ${currentToken}`,
            },
          });

          const data = await response.json();
          if (data.valid && data.user) {
            setToken(currentToken);
            setUser(data.user);
          } else {
            clearSession();
          }
        }
      } catch (error) {
        console.error('Ошибка при загрузке сессии:', error);
        clearSession();
      } finally {
        setIsLoading(false);
      }
    };

    loadSession();
  }, []);

  const clearSession = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authUser');
    setToken(null);
    setUser(null);
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await fetch(getApiUrl(config.api.endpoints.auth.login), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, rememberMe }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Ошибка авторизации');
      }

      const data = await response.json();
      const { token: newToken, user: newUser } = data;

      setToken(newToken);
      setUser(newUser);

      if (rememberMe) {
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('authUser', JSON.stringify(newUser));
        localStorage.setItem('rememberMe', 'true');
      } else {
        sessionStorage.setItem('authToken', newToken);
        sessionStorage.setItem('authUser', JSON.stringify(newUser));
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        localStorage.removeItem('rememberMe');
      }

      return { success: true };
    } catch (error) {
      console.error('Ошибка при авторизации:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(getApiUrl(config.api.endpoints.auth.logout), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Ошибка при выходе из системы:', error);
    } finally {
      clearSession();
    }
  };

  const isAuthenticated = () => {
    return user !== null && token !== null;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        login,
        logout,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;

